import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, User, Clock, ArrowRight, Tag } from 'lucide-react';
import Button from '../components/ui/Button';

const Blog: React.FC = () => {
  const featuredPost = {
    title: "The Future of Work: AI and Human Collaboration",
    excerpt: "Explore how artificial intelligence is reshaping the workplace and creating new opportunities for human-AI collaboration across various industries.",
    image: "https://images.pexels.com/photos/8386434/pexels-photo-8386434.jpeg",
    author: "Sarah Johnson",
    date: "February 15, 2025",
    readTime: "8 min read",
    category: "Technology"
  };

  const posts = [
    {
      title: "5 Essential Skills for Remote Work Success",
      excerpt: "Master these key skills to thrive in the growing remote work environment.",
      image: "https://images.pexels.com/photos/3759098/pexels-photo-3759098.jpeg",
      author: "Michael Chen",
      date: "February 12, 2025",
      readTime: "6 min read",
      category: "Career Development"
    },
    {
      title: "Navigating Career Transitions in 2025",
      excerpt: "A comprehensive guide to making successful career changes in today's dynamic job market.",
      image: "https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg",
      author: "Emily Rodriguez",
      date: "February 10, 2025",
      readTime: "7 min read",
      category: "Career Advice"
    },
    {
      title: "The Rise of Green Jobs",
      excerpt: "Discover emerging opportunities in sustainable industries and environmental careers.",
      image: "https://images.pexels.com/photos/3184467/pexels-photo-3184467.jpeg",
      author: "David Kim",
      date: "February 8, 2025",
      readTime: "5 min read",
      category: "Industry Trends"
    }
  ];

  return (
    <main className="bg-gray-50 min-h-screen py-12">
      <div className="container mx-auto px-4">
        {/* Featured Post */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg overflow-hidden mb-12"
        >
          <div className="grid md:grid-cols-2 gap-8">
            <div className="relative h-64 md:h-full">
              <img
                src={featuredPost.image}
                alt={featuredPost.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
            <div className="p-8">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                {featuredPost.category}
              </span>
              <h1 className="text-3xl font-bold text-gray-900 mt-4 mb-4">
                {featuredPost.title}
              </h1>
              <p className="text-gray-600 mb-6">
                {featuredPost.excerpt}
              </p>
              <div className="flex items-center text-sm text-gray-500 mb-6">
                <User className="h-4 w-4 mr-2" />
                <span className="mr-4">{featuredPost.author}</span>
                <Calendar className="h-4 w-4 mr-2" />
                <span className="mr-4">{featuredPost.date}</span>
                <Clock className="h-4 w-4 mr-2" />
                <span>{featuredPost.readTime}</span>
              </div>
              <Button
                icon={<ArrowRight className="h-4 w-4" />}
                iconPosition="right"
              >
                Read More
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Latest Posts */}
        <section>
          <h2 className="text-2xl font-bold text-gray-900 mb-8">Latest Articles</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {posts.map((post, index) => (
              <motion.article
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-md overflow-hidden"
              >
                <div className="relative h-48">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <Tag className="h-4 w-4 text-primary-600 mr-2" />
                    <span className="text-sm font-medium text-primary-600">
                      {post.category}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center text-sm text-gray-500 mb-4">
                    <User className="h-4 w-4 mr-2" />
                    <span className="mr-4">{post.author}</span>
                    <Clock className="h-4 w-4 mr-2" />
                    <span>{post.readTime}</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    icon={<ArrowRight className="h-4 w-4" />}
                    iconPosition="right"
                  >
                    Read Article
                  </Button>
                </div>
              </motion.article>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
};

export default Blog;